import { viewFor, type PieceAction } from '@halvard/rules';
import type { LocalGameStoreApi } from '../game/store';
import type { BotRequest } from './protocol';

/** Asks a bot for a move. In the browser this goes to the Web Worker. */
export type AskBot = (request: Omit<BotRequest, 'id'>) => Promise<PieceAction>;

/** Runs `run` after `ms` milliseconds; returns a function that cancels it. */
export type Schedule = (run: () => void, ms: number) => () => void;

const defaultSchedule: Schedule = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/**
 * Plays the bots' turns: whenever a bot is to move (and the game is not paused), it waits
 * `botDelay`, asks for a move from the bot's own view, and plays it. Returns a function that stops
 * the driver.
 */
export function startBotDriver(
  store: LocalGameStoreApi,
  ask: AskBot,
  schedule: Schedule = defaultSchedule,
): () => void {
  let cancel: (() => void) | undefined;
  let stopped = false;

  const tick = () => {
    const s = store.getState();
    const { game } = s;
    const player = s.players[game.turn];
    if (stopped || s.thinking || game.winReason !== null || player.kind !== 'bot') return;
    if (s.paused && !s.stepPending) return;
    s.setThinking(true);
    const ply = game.ply;
    cancel = schedule(() => {
      ask({ view: viewFor(game, game.turn), spec: player.spec, seed: s.botSeed * 100_003 + ply })
        .then((choice) => {
          // Undo or a new game happened meanwhile: this answer is for a position that is gone.
          if (stopped || store.getState().game !== game) return;
          store.getState().playBot(choice, ply);
        })
        .catch((error: unknown) => {
          console.error('Bot failed:', error);
          if (!stopped && store.getState().game === game) store.getState().setThinking(false);
        });
    }, s.botDelay);
  };

  const unsubscribe = store.subscribe(tick);
  tick();
  return () => {
    stopped = true;
    unsubscribe();
    cancel?.();
    store.getState().setThinking(false);
  };
}
